/* A project's Subcontractors tab (Duncan 2026-10-21, mockup v2): who's on the job -- every sub picked as a
   task's Responsible Party on the schedule, and every sub CE has under subcontract on it ("no tasks yet"
   until it has some) -- with their tasks, progress, contact and documents. The tasks no sub is on can be
   assigned to one, several at once (POST /api/tasks/assign: the schedule's rules -- a sub marked Do not use can't
   be picked); a task goes to another sub on the Schedule, as its Responsible Party (Duncan 2026-10-29: a sub's Tasks
   tab no longer moves them). Anyone who can open the project.
   Each sub's documents for this job (Duncan 2026-10-22): its certificate of insurance and job site safety plan,
   added and edited here by anyone on the job (JobDocuments.js); its company MSA is flagged, not filed, here.
   Duncan 2026-10-22 ("it needs to look better and more organized and more user-friendly"): a card per sub,
   alphabetical -- who, its progress, what it needs as chips (green in order, amber to follow up, red missing or
   late) -- and its details in tabs, the Schedule first: Schedule, Tasks, Documents, Contact.
   Duncan 2026-10-22: a chip goes to "the spot where to file" what it flags -- its row on the Documents tab,
   highlighted (late tasks: the Tasks tab). The Documents tab is this job's checklist (SubDocRows.js): its
   certificate (its checks a click to change) and its safety plan. The MSA and W-9 aren't a job's: "they are just
   both required for the subcontractor to do work" and collected in Subcontractor Mgmt "before the work is even
   assigned" -- a missing MSA is flagged here, and its chip opens the sub's page there.
   Duncan 2026-10-27 (the redesign he OK'd, all four parts): the list filters to Needs attention / In order / All and
   sorts with the subs that need something first (the search is the bar at the top, as on every tab); a card shows the
   sub's monogram, call and email, the task it's on now (or next); its papers -- COI, safety plan, MSA, W-9 -- in the
   same four spots on every card; and what's waiting on its work on one line under it. A click lifts the card out of
   the list over the blurred page and grows it to fill the window, its tabs inside ("poping out the sub that you click
   on and making it larger in the screen"; the "tasks with no subcontractor" too; no arrows between subs, his pick). */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getProjectSubcontractors, assignTasksToSub, uploadJobDocument, updateSubcontractorDocument, removeSubcontractorDocument,
  downloadSubcontractorDocument, getProjectWorkProgress, setSubContractAmount,
} from '../../api';
import { Icon, Avatar, SegmentedControl, useHoverTip } from '../ui';
import { card, label as microLabel, StandingPill, ErrorBanner, SubStyles, SecondaryButton } from './SubUi';
import { usePageSearch, noMatchText } from '../../lib/pageSearch';
import ResponsiblePartyField from './ResponsiblePartyField';
import { JobDocDialog } from './JobDocuments';
import { JobDocRows, CompanyDocRows, DocSection } from './SubDocRows';
import DocViewer from './DocViewer';
import TaskWorkDialog from './TaskWorkDialog';
import SubRequests from './SubRequests';
import SubBilling from './SubBilling';
import Lift from './Lift';
import { getTaskTypeColor, ZOOM_LEVELS, ganttTimeHeaders, GANTT_ZOOM_STYLES } from '../GanttTab';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import { useProjectToday } from '../../lib/projectToday';
import { coiState, followUpLine, jobDocNeeds, focusOfNeed, expiryStatus, fmtDay, fmtShortDay, fmtSpan, todayYmd, needsDocs, jobDocKindOf } from '../../lib/subcontractors';
import { can } from '../../permissions';

// A sub card's head: who, what it's on and its progress, its four papers, the chevron.
const HEAD_COLS = 'minmax(0, 1.2fr) minmax(0, 1.4fr) auto 16px';
// Billing (Duncan 2026-10-29): the sub's subcontract amount and its schedule of values (SubBilling.js), after Tasks.
const TABS = [['schedule', 'Schedule'], ['tasks', 'Tasks'], ['billing', 'Billing'], ['requests', 'Requests and notes'], ['documents', 'Documents'], ['contact', 'Contact']];
// A task's row: its tick, code, name (and who's on it), dates, progress on E3 Subs, state and % done.
const TASK_COLS = '22px 84px minmax(0, 1.8fr) minmax(0, 1.05fr) minmax(0, 1fr) 136px 46px';
const TASK_COLS_PLAIN = TASK_COLS.slice('22px '.length); // a sub's own tasks: no tick boxes (2026-10-29)
const NONE = 'none'; // the group key of the tasks no sub is on
const NO_FOLDS = new Set(); // no building folded

const pctOf = (t) => Math.max(0, Math.min(100, Number(t.percent_complete) || 0));
const weightOf = (t) => Math.max(1, Number(t.duration_days) || 1);
/** % done of some tasks, weighted by each task's days. */
export const doneOf = (tasks) => {
  const w = tasks.reduce((s, t) => s + weightOf(t), 0);
  return w ? Math.round(tasks.reduce((s, t) => s + pctOf(t) * weightOf(t), 0) / w) : 0;
};
const day = (d) => (d ? String(d).slice(0, 10) : null);
// Late: past its finish and not done -- by E3 Subs' progress where it has it (`fills`: Duncan 2026-10-27, "Use E3 Subs'
// progress"), else the schedule's %.
const isLate = (t, today, fills) => !!day(t.finish_date) && day(t.finish_date) < today && (fills ? workPct(t, fills) : pctOf(t)) < 100;
// A Documents task with none yet (2026-10-24): progress is E3 Subs' per task (tasks.progress), its `documents` count.
const docsDue = (t, today, progress) => needsDocs(t, today, progress && progress[t.id] && progress[t.id].documents);
// A sub's photo reports waiting on this job (E3 Subs v1.19, Duncan's "wait, and flag it in Summit"): its finished lines'
// reports E3 Subs can't file yet, as the job has no closeout Documents task for the sub.
const reportsWaiting = (sub, progress) => (progress
  ? sub.tasks.reduce((n, t) => n + ((progress[t.id] && progress[t.id].photoReports && progress[t.id].photoReports.waiting) || 0), 0) : 0);
const spanOf = (tasks) => {
  const starts = tasks.map((t) => day(t.start_date)).filter(Boolean).sort();
  const ends = tasks.map((t) => day(t.finish_date)).filter(Boolean).sort();
  return starts.length || ends.length ? [starts[0] || null, ends[ends.length - 1] || null] : null;
};

/* The sub side's progress on a task, from E3 Subs: its fixtures installed of its fixtures (2026-10-23; Partly done lines
   count, 10-24 -- before E3 Subs sends that, complete lines), or a Documents task's progress slider (10-25). */
const fixturesOf = (fills, t) => (fills && fills[t.id] && fills[t.id].fixtures > 0 ? fills[t.id] : null);
const installedOf = (f) => (Number.isFinite(f.fixturesInstalled) ? f.fixturesInstalled : f.fixturesDone);
const docsPctOf = (fills, t) => (t.task_type === 'Documents' && fills && fills[t.id] && Number.isFinite(fills[t.id].documentsPercent)
  ? fills[t.id].documentsPercent : null);
/** How far along a task is: a Documents task's slider, else its fixtures installed, else its % done. */
const workPct = (t, fills) => {
  const d = docsPctOf(fills, t);
  if (d != null) return d;
  const f = fixturesOf(fills, t);
  return f ? Math.max(0, Math.min(100, Math.round((installedOf(f) / f.fixtures) * 100))) : pctOf(t);
};
/** How far along some tasks are, each weighted by its days (as doneOf), by what E3 Subs says where it says it. */
const progressOf = (tasks, fills) => {
  const w = tasks.reduce((s, t) => s + weightOf(t), 0);
  return w ? Math.round(tasks.reduce((s, t) => s + workPct(t, fills) * weightOf(t), 0) / w) : 0;
};

/* A sub's tasks by building or area (Duncan 2026-10-27, "Grouped by building": its Schedule and Tasks), in the order the
   work starts there. */
const NO_PLACE = 'No location';
const placeOf = (t) => String(t.building_area_phase || '').trim() || NO_PLACE;
function byPlace(tasks) {
  const map = new Map();
  tasks.forEach((t) => { const k = placeOf(t); if (!map.has(k)) map.set(k, []); map.get(k).push(t); });
  const startOf = (list) => list.map((t) => day(t.start_date) || '9999-12-31').sort()[0];
  return [...map.entries()].map(([place, list]) => ({ place, tasks: list, start: startOf(list), span: spanOf(list) }))
    .sort((a, b) => a.start.localeCompare(b.start) || a.place.localeCompare(b.place));
}

/* A task's state on the Tasks tab (Duncan 2026-10-27, "Tasks at a glance"): done, late, no work or no documents on it yet,
   in progress (begun, or its start date come), not started. */
const STATUS = {
  done: { text: 'Done', tone: 'ok' },
  late: { text: 'Late', tone: 'bad' },
  nowork: { text: 'No work uploaded', tone: 'warn', title: 'No workbook yet: open it to import one' },
  nodocs: { text: 'No documents yet', tone: 'warn', title: 'The sub uploads them on E3 Subs' },
  progress: { text: 'In progress', tone: 'info' },
  notyet: { text: 'Not started', tone: 'na' },
};
function statusOf(t, today, fills) {
  const pct = workPct(t, fills);
  if (pct >= 100) return 'done';
  if (isLate(t, today, fills)) return 'late';
  if (t.needs_work) return 'nowork';
  if (docsDue(t, today, fills)) return 'nodocs';
  return pct > 0 || (day(t.start_date) && day(t.start_date) <= today) ? 'progress' : 'notyet';
}
const FILTERS = [['all', 'All'], ['late', 'Late'], ['work', 'Needs work'], ['progress', 'In progress'], ['notyet', 'Not started'], ['done', 'Done']];
const inFilter = (st, f) => f === 'all' || st === f || (f === 'work' && (st === 'nowork' || st === 'nodocs'));
// A sub's tasks that are its closeout papers: a Documents task, or one whose work is the sub's documents (workNeeded.js).
const isDocTask = (t) => t.task_type === 'Documents' || !!t.docs_expected;

// An MSA's term (2026-10-27, its Documents card as in Subcontractor Mgmt): 'ok', 'expiring' (30 days) or 'expired'.
const msaLevelOf = (msa, today) => (msa && msa.expiry_date ? expiryStatus(msa.expiry_date, today).level : 'ok');

/** What a sub on this job needs: its standing, a missing MSA or W-9 (Duncan 2026-10-22: both "required for the
    subcontractor to do work"), this job's certificate of insurance and safety plan (while the job isn't done),
    late tasks, tasks with no work or no documents on them yet (2026-10-24; server/workNeeded.js). */
export function needsOf(sub, today, job, progress) {
  const out = [];
  if (sub.standing === 'do_not_use') out.push({ text: 'Do not use', tone: 'var(--danger)' });
  if (sub.standing === 'probation') out.push({ text: 'Probation', tone: 'var(--warning)' });
  if (!sub.msa) out.push({ text: 'No MSA', tone: 'var(--danger)' });
  else if (msaLevelOf(sub.msa, today) !== 'ok') out.push({ text: `MSA ${expiryStatus(sub.msa.expiry_date, today).text}`, tone: 'var(--warning)' });
  if (!sub.w9) out.push({ text: 'No W-9', tone: 'var(--danger)' });
  if (!job || job.needs_papers) jobDocNeeds(sub.documents, today).forEach((n) => out.push({ text: n.text, tone: n.tone }));
  const late = sub.tasks.filter((t) => isLate(t, today, progress)).length;
  if (late) out.push({ text: `${late} late`, tone: 'var(--danger)' });
  const bare = sub.tasks.filter((t) => t.needs_work).length;
  if (bare) out.push({ text: `${bare} with no work`, tone: 'var(--warning)' });
  const noDocs = sub.tasks.filter((t) => docsDue(t, today, progress)).length;
  if (noDocs) out.push({ text: `${noDocs} with no documents`, tone: 'var(--warning)' });
  const waiting = reportsWaiting(sub, progress);
  if (waiting) out.push({ text: `${waiting} photo report${waiting === 1 ? '' : 's'} waiting`, tone: 'var(--warning)' });
  return out;
}

const Chevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const Progress = ({ pct }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ flex: 1, maxWidth: 220, height: 6, borderRadius: 4, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
      <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 4 }} />
    </span>
    <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
  </span>
);

/** One of a sub card's tabs: text, the open one bold and underlined in the accent (2026-10-24). */
const CardTab = ({ label, active, onClick }) => (
  <button type="button" role="tab" aria-selected={active} onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', border: 'none', borderBottom: `2.5px solid ${active ? 'var(--accent)' : 'transparent'}`,
      background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: active ? 650 : 500, whiteSpace: 'nowrap',
      color: active ? 'var(--text-1)' : 'var(--text-3)', padding: '4px 1px 8px',
    }}>
    {label}
  </button>
);

const TONE = {
  ok: { color: 'var(--success)', bg: 'var(--success-soft)', icon: 'check' },
  warn: { color: 'var(--warning)', bg: 'var(--warning-soft)', icon: 'alert-triangle' },
  bad: { color: 'var(--danger)', bg: 'var(--danger-soft)', icon: 'alert' },
  na: { color: 'var(--text-4)', bg: 'var(--bg-surface-2)', icon: null },
  info: { color: 'var(--accent-text)', bg: 'var(--accent-soft)', icon: null },
};

/** A task's state as a pill (STATUS). */
const TaskState = ({ st }) => {
  const s = STATUS[st];
  const t = TONE[s.tone];
  return (
    <span title={s.title} style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      color: s.tone === 'na' ? 'var(--text-3)' : t.color, background: t.bg, border: `1px solid color-mix(in oklch, ${s.tone === 'na' ? 'var(--text-3)' : t.color} 20%, transparent)`,
    }}>{s.text}</span>
  );
};

/** A sub's papers in the same four spots on every card (Duncan 2026-10-27): this job's certificate of insurance and its
    safety plan while the job isn't done, and its company MSA and W-9. Green on file, amber to follow up or lapsing, red
    missing or lapsed; grey once the job's done and needs no more. */
function papersOf(sub, today, job) {
  const needed = !job || job.needs_papers;
  const done = 'The job is done: nothing more is needed';
  const coi = coiState(sub.documents, today);
  const follow = followUpLine(coi.followUps);
  const plan = sub.documents.some((d) => d.doc_type === 'safety');
  return [
    needed
      ? { key: 'coi', label: 'COI', tone: coi.level === 'current' ? (follow ? 'warn' : 'ok') : coi.level === 'expiring' ? 'warn' : 'bad',
        title: [coi.text, follow && (follow.title || follow.text)].filter(Boolean).join(' · ') }
      : { key: 'coi', label: 'COI', tone: 'na', title: done },
    needed ? { key: 'safety', label: 'Safety', tone: plan ? 'ok' : 'bad', title: plan ? 'Safety plan on file' : 'No safety plan' }
      : { key: 'safety', label: 'Safety', tone: 'na', title: done },
    sub.msa
      ? { key: 'msa', label: 'MSA', tone: { expired: 'bad', expiring: 'warn' }[msaLevelOf(sub.msa, today)] || 'ok',
        title: msaLevelOf(sub.msa, today) === 'ok' ? 'MSA on file' : `MSA ${expiryStatus(sub.msa.expiry_date, today).text}` }
      : { key: 'msa', label: 'MSA', tone: 'bad', title: 'No MSA' },
    { key: 'w9', label: 'W-9', tone: sub.w9 ? 'ok' : 'bad', title: sub.w9 ? 'W-9 on file' : 'No W-9' },
  ];
}

/** One of a card's four papers: its colour, icon and name. A click goes where it's filed (the card's Documents row, or
    the sub's page in Subcontractor Mgmt for its MSA and W-9). */
const PaperBox = ({ paper, onClick, href }) => {
  const t = TONE[paper.tone];
  const style = {
    display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, width: 50, height: 40,
    padding: 0, boxSizing: 'border-box', borderRadius: 8, fontFamily: 'inherit', fontSize: 11.5, fontWeight: 650, lineHeight: 1.1,
    color: t.color, background: t.bg, border: `1px solid color-mix(in oklch, ${t.color} 22%, transparent)`, textDecoration: 'none',
    cursor: onClick || href ? 'pointer' : 'default',
  };
  const inner = <>{t.icon ? <Icon name={t.icon} size={13} stroke={2} /> : <span aria-hidden="true">–</span>}<span>{paper.label}</span></>;
  const aria = `${paper.label}: ${paper.title}`;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" title={paper.title} aria-label={aria} onClick={(e) => e.stopPropagation()} style={style}>{inner}</a>;
  if (onClick) return <button type="button" title={paper.title} aria-label={aria} onClick={(e) => { e.stopPropagation(); onClick(); }} style={style}>{inner}</button>;
  return <span title={paper.title} aria-label={aria} style={style}>{inner}</span>;
};

/** What's waiting on a sub's work on this job (Duncan 2026-10-27), for the line under its card, the red first: late
    tasks, a certificate that's lapsed or doesn't name the job; then its open requests, tasks with no work or no
    documents yet, photo reports waiting, a certificate lapsing soon and what to follow up on it. A missing paper is
    its box alone. */
function todosOf(sub, today, job, progress, openN) {
  const s = (n) => (n === 1 ? '' : 's');
  const out = [];
  const late = sub.tasks.filter((t) => isLate(t, today, progress)).length;
  if (late) out.push({ key: 'late', tone: 'bad', text: `${late} late task${s(late)}`, title: 'Show the tasks' });
  const coi = !job || job.needs_papers ? coiState(sub.documents, today) : null;
  if (coi && (coi.level === 'expired' || coi.level === 'unnamed')) out.push({ key: 'coi', tone: 'bad', text: coi.text, title: 'Show where it’s filed' });
  if (openN) out.push({ key: 'requests', tone: 'warn', text: `${openN} open request${s(openN)}`, title: 'Show its requests and notes' });
  const bare = sub.tasks.filter((t) => t.needs_work).length;
  if (bare) out.push({ key: 'work', tone: 'warn', text: `${bare} task${s(bare)} with no work`, title: 'Show the tasks: open one to import its workbook' });
  const noDocs = sub.tasks.filter((t) => docsDue(t, today, progress)).length;
  if (noDocs) out.push({ key: 'docs', tone: 'warn', text: `${noDocs} task${s(noDocs)} with no documents`, title: 'Show the tasks: the sub uploads their documents on E3 Subs' });
  const waiting = reportsWaiting(sub, progress);
  if (waiting) out.push({ key: 'reports', tone: 'warn', text: `${waiting} photo report${s(waiting)} waiting`, title: 'Show what to add: a closeout Documents task for this sub' });
  if (coi && coi.level === 'expiring') out.push({ key: 'coi', tone: 'warn', text: coi.text, title: 'Show where it’s filed' });
  const follow = coi ? followUpLine(coi.followUps) : null;
  if (follow) out.push({ key: 'coi-follow', tone: 'warn', text: follow.text, title: follow.title || 'Show where it’s filed' });
  return out;
}

/** A sub's monogram: the first letters of its name's first two words. */
const initialsOf = (name) => String(name || '').split(/[\s,&/-]+/).filter((w) => /^[A-Za-z0-9]/.test(w))
  .slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

/** What a sub is on (Duncan 2026-10-27): the task under way today (the first to finish, and how many more are), else
    the next to start, else its oldest late one; "done" once every dated task is complete -- by E3 Subs' progress where
    it has it (workPct). Milestones aside. */
function nowNextOf(tasks, today, fills) {
  const dated = tasks.filter((t) => day(t.start_date) && day(t.finish_date) && !(Number(t.milestone_flag) === 1 || t.task_type === 'Milestone'));
  const open = dated.filter((t) => workPct(t, fills) < 100);
  const by = (key) => (a, b) => day(a[key]).localeCompare(day(b[key]));
  const now = open.filter((t) => day(t.start_date) <= today && day(t.finish_date) >= today).sort(by('finish_date'));
  if (now.length) return { kind: 'now', task: now[0], more: now.length - 1 };
  const next = open.filter((t) => day(t.start_date) > today).sort(by('start_date'));
  if (next.length) return { kind: 'next', task: next[0], more: 0 };
  const late = open.filter((t) => day(t.finish_date) < today).sort(by('finish_date'));
  if (late.length) return { kind: 'late', task: late[0], more: late.length - 1 };
  return dated.length ? { kind: 'done', task: null, more: 0 } : null;
}

const NOW_TAG = { now: ['Now', 'var(--accent-text)'], next: ['Next', 'var(--text-3)'], late: ['Late', 'var(--danger)'], done: ['Done', 'var(--success)'] };
/** A card's "Now · Lighting Retrofit · Lone Star MS · ends Mar 3": the task's name opens its work. */
function NowNext({ nn, today, onOpenTask }) {
  if (!nn) return null;
  const [tag, color] = NOW_TAG[nn.kind];
  const t = nn.task;
  const when = !t ? 'every task is complete'
    : nn.kind === 'next' ? `starts ${fmtShortDay(day(t.start_date), today)}`
      : `${nn.kind === 'late' ? 'was due' : 'ends'} ${fmtShortDay(day(t.finish_date), today)}`;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0, fontSize: 13, lineHeight: 1.35 }}>
      <span style={{ fontWeight: 650, color, flexShrink: 0 }}>{tag}</span>
      {t && (
        <button type="button" className="psub-task" title={`Open its work: ${t.task_name}${t.building_area_phase ? ` · ${t.building_area_phase}` : ''}`}
          onClick={(e) => { e.stopPropagation(); onOpenTask(t.id); }}
          style={{
            minWidth: 0, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
            textAlign: 'left', color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
          {t.task_name}{t.building_area_phase ? <span style={{ color: 'var(--text-3)' }}> · {t.building_area_phase}</span> : null}
        </button>
      )}
      <span style={{ flexShrink: 0, fontSize: 12.5, color: nn.kind === 'late' ? 'var(--danger)' : 'var(--text-3)' }}>
        {t ? '· ' : ''}{when}{nn.more ? ` · +${nn.more} more` : ''}
      </span>
    </div>
  );
}

/** The subs' order (Duncan 2026-10-27: those that need something first). */
const ORDERS = [['attention', 'Needs attention first'], ['name', 'Name'], ['soonest', 'Working soonest']];
const whenOf = (s, today) => {
  const nn = s.nowNext;
  if (!nn) return '9999-12-31';
  if (nn.kind === 'now' || nn.kind === 'late') return today;
  return nn.kind === 'next' ? day(nn.task.start_date) : '9999-12-30';
};

const emptyNote = { padding: '14px 16px', borderRadius: 10, border: '1px dashed var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13.5, color: 'var(--text-3)' };

const sortStyle = {
  height: 32, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)',
  color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit',
};

const pickerStyle = {
  width: '100%', height: 32, padding: '0 10px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
  borderRadius: 8, fontSize: 13.5, color: 'var(--text-1)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};

const GANTT_LABEL = 280;
const GANTT_ROW = 38;
const GANTT_HEAD = 56; // the Schedule's two header rows: 24 + 32
const BAR_H = 22; // the Schedule's bar height, room for its name and % inside
const SUB_ZOOM = ZOOM_LEVELS.findIndex((z) => z.key === 'month'); // opens on Month: about its whole span, as before
const DAY_MS = 24 * 60 * 60 * 1000;
const dayAt = (ymd) => new Date(`${ymd}T00:00:00`);
const oneLine = { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
// A task's name shows it opens; on a phone the names column narrows so the timeline keeps room.
const SUB_GANTT_CSS = `
  .subgantt-name:hover { background: color-mix(in oklch, var(--bg-hover) 70%, transparent) !important; }
  .subgantt-name:hover .subgantt-title { color: var(--accent-text); }
  .subgantt-name:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
  @media (max-width: 700px) { .subgantt { grid-template-columns: 150px minmax(0, 1fr) !important; } }
`;

/** A sub's schedule (Duncan 2026-10-21): its own tasks, earliest first, in the Schedule's bar look (its type
    colour, % done filling the bar, a late one red, a milestone a diamond), with the Schedule tab's Today button,
    Day / Week / Month / Quarter zoom, header and today line (10-23: "the same sorting in appearance features as
    the schedule tab"). It spans its tasks and today, 14 days either side as the Schedule does, so the line always
    shows. A bar or a task's name opens its work (the name too, 10-23); hovering either gives its dates. A bar fills with
    the sub side's progress when its task has line work (10-23: "from whatever the sub side is" -- fixtures installed
    of the task's fixtures, from E3 Subs), else its % done as before. The view settings sit on the left (10-23).
    By building or area (Duncan 2026-10-27, "Grouped by building"): a heading row for each -- its count, its progress
    and a bar for its span -- in the order the work starts there; a click on one folds it (`folded`, shared with the
    Tasks tab). */
function SubGantt({ tasks, today, fills, onOpenTask, folded, onFold }) {
  // Its bars' and names' lines in an instant pop-up, as the Schedule Gantt's (Duncan 2026-10-28; ui/HoverTip.js)
  const [tip, tipOn] = useHoverTip({ maxWidth: 340 });
  const [zoomLevel, setZoomLevel] = useState(SUB_ZOOM);
  const [paneW, setPaneW] = useState(0);
  const paneRef = useRef(null);
  const placedFor = useRef(null); // the zoom the timeline was last scrolled into place for
  const rows = useMemo(() => tasks.filter((t) => day(t.start_date) && day(t.finish_date))
    .sort((a, b) => day(a.start_date).localeCompare(day(b.start_date)) || day(a.finish_date).localeCompare(day(b.finish_date))), [tasks]);
  // Each building's heading, then its tasks (none while it's folded).
  const items = useMemo(() => byPlace(rows).flatMap((g) => [{ g }, ...(folded && folded.has(g.place) ? [] : g.tasks.map((t) => ({ t })))]), [rows, folded]);
  const hasRows = rows.length > 0;
  const span = useMemo(() => {
    if (!rows.length) return null;
    const first = day(rows[0].start_date);
    const last = rows.map((t) => day(t.finish_date)).sort().pop();
    const start = dayAt(today < first ? today : first);
    start.setDate(start.getDate() - 14);
    const end = dayAt(today > last ? today : last);
    end.setDate(end.getDate() + 14);
    return { first, last, start, days: Math.round((end - start) / DAY_MS) + 1 };
  }, [rows, today]);
  const zoom = ZOOM_LEVELS[zoomLevel];
  // At least as wide as the timeline's box, as the Schedule fills its view.
  const totalDays = span ? Math.max(span.days, Math.ceil(paneW / zoom.dayWidth)) : 0;
  const headers = useMemo(() => {
    if (!span) return { primary: [], secondary: [] };
    const end = new Date(span.start);
    end.setDate(end.getDate() + totalDays);
    return ganttTimeHeaders(span.start, end, totalDays, zoom);
  }, [span, totalDays, zoom]);
  const xOf = useCallback((ymd) => (span ? Math.round((dayAt(ymd) - span.start) / DAY_MS) * zoom.dayWidth : 0), [span, zoom]);

  // The timeline box's width, kept as the page resizes.
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    setPaneW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setPaneW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasRows]);
  // Into place on opening and on each zoom: today in the middle while it falls in their work (as the Schedule
  // tab); before or after it, today beside their work's start or end when both fit, else their work.
  useEffect(() => {
    const el = paneRef.current;
    if (!el || !span || !paneW || placedFor.current === zoomLevel) return;
    placedFor.current = zoomLevel;
    const w = el.clientWidth;
    const t = xOf(today);
    const a = xOf(span.first);
    const b = xOf(span.last) + zoom.dayWidth;
    let left;
    if (today >= span.first && today <= span.last) left = t - w / 2;
    else if (today < span.first) left = a - t <= w - 120 ? t - 48 : a - 24;
    else left = t - b <= w - 120 ? t - w + 48 : b - w + 24;
    el.scrollLeft = Math.max(0, left);
  }, [zoomLevel, paneW, span, today, xOf, zoom]);
  const toToday = () => {
    const el = paneRef.current;
    if (el) el.scrollLeft = xOf(today) - el.clientWidth / 2;
  };

  if (!hasRows) return null;
  const chartW = totalDays * zoom.dayWidth;
  const todayX = xOf(today);
  // A bar fills with E3 Subs' progress where it has it (workPct): fixtures installed, or a Documents task's slider.
  const fillOf = (t) => fixturesOf(fills, t);
  const docPct = (t) => docsPctOf(fills, t);
  const barPct = (t) => workPct(t, fills);
  const tipOf = (t) => {
    const f = fillOf(t);
    const sub = docPct(t) != null ? `\nOn E3 Subs: documents ${docPct(t)}% along`
      : f ? `\nOn E3 Subs: ${installedOf(f).toLocaleString()} of ${f.fixtures.toLocaleString()} fixtures installed (${barPct(t)}%) · ${f.linesDone} of ${f.lines} lines complete` : '';
    const people = fills && fills[t.id] && fills[t.id].technicians && fills[t.id].technicians.length
      ? `\nTechnicians: ${fills[t.id].technicians.map((x) => x.name).join(', ')}` : '';
    const bare = t.needs_work ? '\nNo work uploaded yet: open it to import its workbook'
      : docsDue(t, today, fills) ? '\nNo documents yet: the sub uploads them on E3 Subs' : '';
    return `${t.task_code ? `${t.task_code} · ` : ''}${t.task_name}${t.building_area_phase ? ` · ${t.building_area_phase}` : ''}\n${fmtSpan(day(t.start_date), day(t.finish_date), today)} · ${pctOf(t)}% done${isLate(t, today, fills) ? ' · late' : ''}${bare}${sub}${people}`;
  };
  const anyFill = rows.some((t) => fillOf(t));
  const anyDocs = rows.some((t) => docPct(t) != null);
  return (
    // One framed panel (Duncan 2026-10-24: the card's tabs, the Gantt's buttons and the chart ran together): its
    // buttons are the panel's header, the chart under them.
    <div style={{ margin: '2px 0 14px', border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      <style>{SUB_GANTT_CSS}</style>
      {tip}
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)' }}>
        <button type="button" style={GANTT_ZOOM_STYLES.todayBtn} onClick={toToday} title="Scroll to today">Today</button>
        <div style={GANTT_ZOOM_STYLES.zoomGroup} role="group" aria-label="Zoom">
          {ZOOM_LEVELS.map((lvl, i) => (
            <button key={lvl.key} type="button" aria-pressed={i === zoomLevel} onClick={() => setZoomLevel(i)}
              style={{
                ...GANTT_ZOOM_STYLES.zoomBtn,
                ...(i === zoomLevel ? GANTT_ZOOM_STYLES.zoomBtnActive : {}),
                borderRadius: i === 0 ? '6px 0 0 6px' : i === ZOOM_LEVELS.length - 1 ? '0 6px 6px 0' : '0',
              }}>
              {lvl.label}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        {(anyFill || anyDocs) && <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Bars fill with {[anyFill ? 'fixtures installed' : null, anyDocs ? 'documents’ progress' : null].filter(Boolean).join(' and ')}, from E3 Subs</span>}
      </div>
      <div className="subgantt" style={{ display: 'grid', gridTemplateColumns: `${GANTT_LABEL}px minmax(0, 1fr)`, background: 'var(--bg-surface)', overflow: 'hidden' }}>
        {/* The names, still while the timeline scrolls. */}
        <div style={{ minWidth: 0, borderRight: '1px solid var(--border-subtle)' }}>
          <div style={{ height: GANTT_HEAD, boxSizing: 'border-box', display: 'flex', alignItems: 'flex-end', padding: '0 10px 10px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
            <span style={{ ...microLabel, fontSize: 10.5 }}>Their schedule</span>
          </div>
          {/* A building's heading (a click folds it), then its tasks: the name, then code · dates. */}
          {items.map(({ g, t }, i) => (g ? (
            <button key={`g-${g.place}`} type="button" className="subgantt-name" aria-expanded={!(folded && folded.has(g.place))} onClick={() => onFold(g.place)}
              title={folded && folded.has(g.place) ? `Show the tasks at ${g.place}` : `Fold the tasks at ${g.place}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%', height: GANTT_ROW, boxSizing: 'border-box', padding: '0 10px', border: 'none',
                borderTop: i ? '1px solid var(--border-default)' : 'none', borderRadius: 0, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                background: 'var(--bg-surface-2)',
              }}>
              <Chevron open={!(folded && folded.has(g.place))} />
              <span className="subgantt-title" style={{ ...oneLine, flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 650, color: 'var(--text-1)' }}>{g.place}</span>
              <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{g.tasks.length}</span>
              <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)', minWidth: 34, textAlign: 'right' }}>{progressOf(g.tasks, fills)}%</span>
            </button>
          ) : (
            <button key={t.id} type="button" className="subgantt-name" {...tipOn(tipOf(t))} onClick={() => onOpenTask(t.id)}
              aria-label={`Open the work for ${t.task_name}${t.task_code ? `, ${t.task_code}` : ''}`}
              style={{
                display: 'block', width: '100%', height: GANTT_ROW, boxSizing: 'border-box', padding: '0 10px 0 30px', border: 'none', borderRadius: 0,
                textAlign: 'left', lineHeight: 1.25, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent',
              }}>
              <span className="subgantt-title" style={{ ...oneLine, fontSize: 12.5, color: 'var(--text-1)' }}>
                {(t.needs_work || docsDue(t, today, fills)) && <span style={{ color: 'var(--warning)', marginRight: 4, verticalAlign: '-2px' }}><Icon name="alert-triangle" size={12} stroke={2} /></span>}
                {t.task_name}
              </span>
              <span style={{ ...oneLine, fontSize: 11.5, color: 'var(--text-3)' }}>
                <span className="mono">{t.task_code || '—'}</span> · {fmtSpan(day(t.start_date), day(t.finish_date), today)}
              </span>
            </button>
          )))}
        </div>
        {/* The timeline: the Schedule's header, grid and today line, scrolling sideways. */}
        <div ref={paneRef} style={{ minWidth: 0, overflowX: 'auto', overflowY: 'hidden' }}>
          <div style={{ width: chartW, position: 'relative' }}>
            <div style={{ height: GANTT_HEAD, boxSizing: 'border-box', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
              <div style={{ position: 'relative', height: 24, borderBottom: '1px solid var(--border-subtle)' }}>
                {headers.primary.map((h, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: h.left, width: h.width, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRight: '1px solid var(--border-subtle)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '-0.005em',
                    whiteSpace: 'nowrap', overflow: 'hidden',
                  }}>{h.label}</div>
                ))}
              </div>
              <div style={{ position: 'relative', height: 'calc(100% - 24px)' }}>
                {headers.secondary.map((h, i) => {
                  const isCurrent = todayX >= h.left && todayX < h.left + h.width;
                  return (
                    <div key={i} className="mono" style={{
                      position: 'absolute', left: h.left, width: h.width, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRight: '1px solid var(--border-subtle)', fontSize: 12, letterSpacing: '-0.01em', overflow: 'hidden',
                      color: isCurrent ? 'var(--accent-text)' : 'var(--text-3)', fontWeight: isCurrent ? 600 : 500,
                      background: isCurrent ? 'var(--accent-soft)' : h.isWeekend ? 'var(--bg-surface-2)' : 'transparent',
                    }}>{h.label}</div>
                  );
                })}
              </div>
            </div>
            <div style={{ position: 'relative', height: items.length * GANTT_ROW }}>
              {items.map(({ g }, i) => (g ? (
                <div key={`r-${g.place}`} style={{
                  position: 'absolute', left: 0, right: 0, top: i * GANTT_ROW, height: GANTT_ROW, boxSizing: 'border-box',
                  background: 'var(--bg-surface-2)', borderTop: i ? '1px solid var(--border-default)' : 'none',
                }} />
              ) : null))}
              {headers.secondary.map((h, i) => (
                <React.Fragment key={`g${i}`}>
                  {h.isWeekend && <div style={{ position: 'absolute', left: h.left, top: 0, bottom: 0, width: h.width, background: 'var(--bg-surface-2)', opacity: 0.5, pointerEvents: 'none' }} />}
                  <div style={{ position: 'absolute', left: h.left, top: 0, bottom: 0, width: 1, background: 'var(--border-subtle)', opacity: 0.6, pointerEvents: 'none' }} />
                </React.Fragment>
              ))}
              {headers.primary.map((h, i) => (i > 0 ? (
                <div key={`m${i}`} style={{ position: 'absolute', left: h.left, top: 0, bottom: 0, width: 1, background: 'var(--border-default)', opacity: 0.7, pointerEvents: 'none' }} />
              ) : null))}
              {items.map(({ g, t }, i) => {
                if (g) {
                  // A building's span as a thin bar, filled as far as its tasks are along.
                  const gl = xOf(g.span[0]);
                  const gw = Math.max(xOf(g.span[1]) + zoom.dayWidth - gl, 4);
                  const gp = progressOf(g.tasks, fills);
                  return (
                    <div key={`b-${g.place}`} aria-hidden="true" title={`${g.place}: ${fmtSpan(g.span[0], g.span[1], today)} · ${gp}% along`}
                      style={{
                        position: 'absolute', left: gl, width: gw, top: i * GANTT_ROW + (GANTT_ROW - 8) / 2, height: 8, borderRadius: 4, zIndex: 2,
                        background: 'color-mix(in oklch, var(--text-3) 22%, transparent)', overflow: 'hidden',
                      }}>
                      <span style={{ display: 'block', width: `${gp}%`, height: '100%', background: gp >= 100 ? 'var(--success)' : 'var(--accent)', opacity: 0.85 }} />
                    </div>
                  );
                }
                const pct = barPct(t);
                const accent = isLate(t, today, fills) ? 'var(--danger)' : getTaskTypeColor(t);
                const fill = pct >= 100 ? 'var(--success)' : accent;
                const left = xOf(day(t.start_date));
                const top = i * GANTT_ROW;
                const milestone = Number(t.milestone_flag) === 1 || t.task_type === 'Milestone';
                const barW = Math.max(xOf(day(t.finish_date)) + zoom.dayWidth - left, 4);
                return milestone ? (
                  <button key={t.id} type="button" {...tipOn(tipOf(t))} aria-label={`Open ${t.task_name}`} onClick={() => onOpenTask(t.id)}
                    style={{ position: 'absolute', left: left - 5, top: top + (GANTT_ROW - 10) / 2, width: 10, height: 10, padding: 0, border: 'none', background: accent, transform: 'rotate(45deg)', cursor: 'pointer', zIndex: 2 }} />
                ) : (
                  // As the Schedule tab's bars (Duncan 2026-10-23): 22 px tall, the name inside flush left and the % flush
                  // right (white over a fill past 40%); a bar too narrow for them shows its % just left of it.
                  <React.Fragment key={t.id}>
                    <button type="button" {...tipOn(tipOf(t))} aria-label={`Open ${t.task_name}`} onClick={() => onOpenTask(t.id)}
                      style={{
                        position: 'absolute', left, width: barW, top: top + 8, height: BAR_H, padding: 0,
                        background: `color-mix(in oklch, ${accent} 12%, transparent)`,
                        border: `1px solid color-mix(in oklch, ${accent} ${pct === 0 ? 22 : 30}%, transparent)`,
                        borderRadius: 5, overflow: 'hidden', cursor: 'pointer', zIndex: 2, fontFamily: 'inherit',
                      }}>
                      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: fill, opacity: 0.85, borderRadius: pct >= 100 ? 4 : '4px 0 0 4px' }} />
                      {barW >= 80 && (
                        <>
                          <span style={{
                            position: 'absolute', left: 7, top: 0, bottom: 0, right: 44, display: 'inline-flex', alignItems: 'center',
                            fontSize: 12, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none',
                          }}>{t.task_name}</span>
                          <span className="mono" style={{
                            position: 'absolute', right: 7, top: 0, bottom: 0, display: 'inline-flex', alignItems: 'center',
                            // white only where the fill reaches under it (a 43% fill leaves the right end pale)
                            fontSize: 12, fontWeight: 500, color: (barW * pct) / 100 >= barW - 40 ? '#fff' : 'var(--text-2)', pointerEvents: 'none',
                          }}>{pct}%</span>
                        </>
                      )}
                    </button>
                    {barW < 80 && (
                      <span className="mono" aria-hidden="true" style={{
                        position: 'absolute', left: left - 4, top: top + 8, transform: 'translateX(-100%)', fontSize: 12, fontWeight: 500,
                        color: 'var(--text-2)', lineHeight: `${BAR_H}px`, whiteSpace: 'nowrap', zIndex: 2, pointerEvents: 'none',
                      }}>{pct}%</span>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Today as on the Schedule: a sage line at 55% and its tag -- never red. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: todayX, top: 0, bottom: 0, width: 1, background: 'var(--accent)', opacity: 0.55, zIndex: 3, pointerEvents: 'none' }} />
              <div aria-hidden="true" className="mono" style={{
                position: 'absolute', left: todayX - 22, top: 2, background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 600,
                letterSpacing: '0.04em', padding: '1px 6px', borderRadius: 3, zIndex: 4, pointerEvents: 'none', whiteSpace: 'nowrap',
              }}>TODAY</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// What an empty filter says.
const NOTHING = { all: 'No tasks.', late: 'Nothing late.', work: 'Nothing needs work.', progress: 'Nothing in progress.', notyet: 'Nothing waiting to start.', done: 'Nothing done yet.' };

/** A list of tasks (Duncan 2026-10-27, "Tasks at a glance" and "Grouped by building"): filters by state with their
    counts; the tasks under each building or area -- a heading with its count, dates and progress, a click folding it
    (shared with the Schedule) -- each with its state, its progress on E3 Subs and who's on it there. Every task shows
    (the lifted card has the room). With `setPicked` (the tasks no sub is on), ticks -- one for each, and for each
    building -- and its Assign bar. */
function TaskList({ tasks, groupKey, picked, setPicked, onOpenTask, today, progress, filter = 'all', onFilter, folded, onFold, children }) {
  // Tick boxes for the tasks no sub is on, to assign them (Duncan 2026-10-29: a sub's own tasks move on the Schedule).
  const tickable = !!setPicked;
  const cols = tickable ? TASK_COLS : TASK_COLS_PLAIN;
  const states = new Map(tasks.map((t) => [t.id, statusOf(t, today, progress)]));
  const countOf = (f) => tasks.filter((t) => inFilter(states.get(t.id), f)).length;
  const shownOf = (list) => list.filter((t) => inFilter(states.get(t.id), filter));
  const visible = shownOf(tasks);
  const allOn = tickable && visible.length > 0 && visible.every((t) => picked.has(t.id));
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tickAll = (list, on) => setPicked((s) => { const n = new Set(s); list.forEach((t) => (on ? n.add(t.id) : n.delete(t.id))); return n; });
  const groups = byPlace(tasks).map((g) => ({ ...g, rows: shownOf(g.tasks) })).filter((g) => g.rows.length);
  const isFolded = (place) => !!folded && folded.has(place);
  const chip = (on) => ({
    padding: '3px 11px', borderRadius: 999, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
    border: `1px solid ${on ? 'var(--border-strong)' : 'transparent'}`,
    background: on ? 'var(--bg-surface)' : 'transparent', color: on ? 'var(--text-1)' : 'var(--text-3)',
  });
  // What E3 Subs says of it: fixtures installed, or a Documents task's files and slider.
  const onSubs = (t) => {
    const f = fixturesOf(progress, t);
    if (f) return `${installedOf(f).toLocaleString()} of ${f.fixtures.toLocaleString()} fixtures`;
    const d = docsPctOf(progress, t);
    if (d != null) { const n = (progress[t.id] && progress[t.id].documents) || 0; return `${n} file${n === 1 ? '' : 's'} · ${d}%`; }
    return '—';
  };
  return (
    <div>
      {children}
      <div role="group" aria-label="Show the tasks" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', margin: '2px 0 10px' }}>
        {FILTERS.map(([k, label]) => {
          const n = countOf(k);
          return (k === 'all' || n > 0 || filter === k) ? (
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => onFilter && onFilter(k)} style={chip(filter === k)}>
              {label} <span className="mono" style={{ fontSize: 12, color: k === 'late' && n ? 'var(--danger)' : undefined }}>{n}</span>
            </button>
          ) : null;
        })}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 760 }}>
          <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
            {tickable && <input type="checkbox" checked={allOn} onChange={() => tickAll(visible, !allOn)} aria-label={allOn ? 'Untick every task shown' : 'Tick every task shown'} style={{ margin: 0 }} />}
            {['Code', 'Task', 'Dates', 'On E3 Subs', 'State', 'Done'].map((h) => <span key={h} style={{ ...microLabel, fontSize: 10.5 }}>{h}</span>)}
          </div>
          {groups.map((g) => {
            const gOn = tickable && g.rows.every((t) => picked.has(t.id));
            const shut = isFolded(g.place);
            return (
              <React.Fragment key={g.place}>
                <div style={{ display: 'grid', gridTemplateColumns: tickable ? '22px minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 10, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                  {tickable && (
                    <input type="checkbox" checked={gOn} onChange={() => tickAll(g.rows, !gOn)} style={{ margin: 0 }}
                      aria-label={gOn ? `Untick the tasks at ${g.place}` : `Tick the tasks at ${g.place}`} />
                  )}
                  <button type="button" aria-expanded={!shut} onClick={() => onFold && onFold(g.place)}
                    title={shut ? `Show the tasks at ${g.place}` : `Fold the tasks at ${g.place}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                    <Chevron open={!shut} />
                    <span style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.place}</span>
                    <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                      {g.rows.length === g.tasks.length ? g.tasks.length : `${g.rows.length} of ${g.tasks.length}`}
                    </span>
                    <span style={{ flex: 1 }} />
                    {g.span && <span style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{fmtSpan(g.span[0], g.span[1], today)}</span>}
                    <span style={{ width: 170, flexShrink: 0 }}><Progress pct={progressOf(g.tasks, progress)} /></span>
                  </button>
                </div>
                {!shut && g.rows.map((t) => {
                  const late = isLate(t, today, progress);
                  const people = progress && progress[t.id] && progress[t.id].technicians;
                  return (
                    <div key={t.id} onDoubleClick={(e) => { if (e.target.type !== 'checkbox') onOpenTask(t.id); }}
                      style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13.5 }}>
                      {tickable && <input type="checkbox" checked={picked.has(t.id)} onChange={() => toggle(t.id)} aria-label={`Tick ${t.task_name}`} style={{ margin: 0 }} />}
                      <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{t.task_code || '—'}</span>
                      <button type="button" onClick={() => onOpenTask(t.id)} title="Open its work"
                        style={{ textAlign: 'left', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--text-1)', fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'inherit', minWidth: 0 }}>
                        {t.task_name}{groupKey === NONE && t.responsible_party ? <span style={{ color: 'var(--text-3)' }}> · {t.responsible_party}</span> : null}
                        {/* Who the sub put on it, on E3 Subs (2026-10-24; names only) */}
                        {people && people.length > 0 && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2, fontSize: 12.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Icon name="team" size={12} />{people.map((x) => x.name).join(', ')}
                          </span>
                        )}
                      </button>
                      <span style={{ color: late ? 'var(--danger)' : 'var(--text-3)', fontWeight: late ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {fmtSpan(day(t.start_date), day(t.finish_date), today) || '—'}
                      </span>
                      <span style={{ color: 'var(--text-2)', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{onSubs(t)}</span>
                      <span><TaskState st={states.get(t.id)} /></span>
                      {/* As far along as its state says (E3 Subs' progress where it has it); the schedule's own % when that differs. */}
                      <span className="mono" title={workPct(t, progress) !== pctOf(t) ? `On the schedule: ${pctOf(t)}%` : undefined}
                        style={{ fontSize: 13, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{workPct(t, progress)}%</span>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {!visible.length && (
        <div style={{ ...emptyNote, marginTop: 10 }}>
          {NOTHING[filter] || 'No tasks.'}{' '}
          <button type="button" onClick={() => onFilter && onFilter('all')} style={{ padding: 0, border: 'none', background: 'transparent', color: 'var(--accent-text)', cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit' }}>Show all</button>
        </div>
      )}
    </div>
  );
}

/** The closeout papers the sub sends on its Documents tasks (Duncan 2026-10-27, "All four papers on Documents"): each
    task with its files and how far along its slider says they are (E3 Subs), opening its documents. */
function CloseoutDocs({ tasks, fills, today, onOpen }) {
  if (!tasks.length) {
    return (
      <div style={{ padding: '12px 14px', fontSize: 13.5, color: 'var(--text-3)' }}>
        No closeout Documents task for this sub on this job. Add one on the Schedule (type Documents) when the job needs O&amp;M manuals,
        as-builts, training records or warranties from them.
      </div>
    );
  }
  return (
    <div style={{ padding: '2px 14px 6px' }}>
      {tasks.map((t, i) => {
        const n = (fills && fills[t.id] && fills[t.id].documents) || 0;
        const pct = docsPctOf(fills, t);
        return (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
            <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}><Icon name="template" size={16} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.task_name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                {[t.task_code, t.building_area_phase, fmtSpan(day(t.start_date), day(t.finish_date), today)].filter(Boolean).join(' · ')}
              </div>
            </div>
            <span style={{ fontSize: 13, color: n ? 'var(--text-2)' : 'var(--text-3)', whiteSpace: 'nowrap' }}>
              {n} file{n === 1 ? '' : 's'}{pct != null ? ` · ${pct}% along` : ''}
            </span>
            {pct != null && <span style={{ width: 120, flexShrink: 0 }}><Progress pct={pct} /></span>}
            <SecondaryButton onClick={() => onOpen(t.id)} style={{ height: 30, fontSize: 13 }}>Open</SecondaryButton>
          </div>
        );
      })}
    </div>
  );
}

// A link that looks like the Secondary button (SubUi.js): Call, Email.
const linkButton = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8,
  fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)', color: 'var(--text-2)', background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', textDecoration: 'none',
};
const panel = { padding: '14px 16px', borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', minWidth: 0 };

/** The sub's contact (Duncan 2026-10-27, "A real Contact card"): who to reach, with Call, Email and Copy; the technicians
    the sub has put on this job's tasks on E3 Subs; its CE subcontracts; its page in Subcontractor Mgmt. */
function ContactCard({ sub, fills, canOpenSubs }) {
  const contact = sub.contact || {};
  const [copied, setCopied] = useState(null);
  useEffect(() => { if (!copied) return undefined; const t = setTimeout(() => setCopied(null), 2000); return () => clearTimeout(t); }, [copied]);
  // Its technicians on this job: each with the tasks they're on, the busiest first.
  const people = new Map();
  sub.tasks.forEach((t) => ((fills && fills[t.id] && fills[t.id].technicians) || []).forEach((x) => {
    if (!people.has(x.name)) people.set(x.name, []);
    people.get(x.name).push(t);
  }));
  const crew = [...people.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  // Copy: 'yes' or 'no' for two seconds after (no clipboard permission -- an embedded or older browser -- tries the old way).
  const copy = async () => {
    const text = [contact.name, contact.title, sub.name, contact.phone, contact.email].filter(Boolean).join('\n');
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch (e) {
      const box = document.createElement('textarea');
      box.value = text;
      box.setAttribute('readonly', '');
      Object.assign(box.style, { position: 'fixed', top: '0', left: '0', opacity: '0' });
      document.body.appendChild(box);
      box.select();
      try { ok = document.execCommand('copy'); } catch (e2) { ok = false; }
      document.body.removeChild(box);
    }
    setCopied(ok ? 'yes' : 'no');
  };
  const has = contact.name || contact.phone || contact.email;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 12, alignItems: 'start' }}>
      <section aria-label="Main contact" style={panel}>
        <div style={{ ...microLabel, fontSize: 10.5, marginBottom: 10 }}>Main contact</div>
        {has ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar initials={initialsOf(contact.name || sub.name)} size={44} accent />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 650, color: 'var(--text-1)' }}>{contact.name || sub.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{[contact.title, sub.name].filter(Boolean).join(' · ')}</div>
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
              {contact.phone && <div className="mono">{contact.phone}</div>}
              {contact.email && <div>{contact.email}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {contact.phone && <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`} style={linkButton}><Icon name="phone" size={14} />Call</a>}
              {contact.email && <a href={`mailto:${contact.email}`} style={linkButton}><Icon name="mail" size={14} />Email</a>}
              <SecondaryButton onClick={copy} style={{ height: 32, fontSize: 13.5 }} aria-live="polite">
                <Icon name={copied === 'yes' ? 'check' : copied === 'no' ? 'alert-triangle' : 'template'} size={14} />
                {copied === 'yes' ? 'Copied' : copied === 'no' ? 'Couldn’t copy' : 'Copy'}
              </SecondaryButton>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>None on file.{canOpenSubs ? ' Add one on the sub’s page in Subcontractor Mgmt.' : ''}</div>
        )}
      </section>
      <section aria-label="On this job, from E3 Subs" style={panel}>
        <div style={{ ...microLabel, fontSize: 10.5, marginBottom: 6 }}>On this job, from E3 Subs</div>
        {crew.length ? crew.map(([name, list], i) => (
          <div key={name} title={list.map((t) => `${t.task_name}${t.building_area_phase ? ` · ${t.building_area_phase}` : ''}`).join('\n')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
            <Avatar initials={initialsOf(name)} size={28} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{list.length} task{list.length === 1 ? '' : 's'}</span>
          </div>
        )) : (
          <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>No one on its tasks yet: the sub puts its technicians on them in E3 Subs.</div>
        )}
      </section>
      <section aria-label="CE and Subcontractor Mgmt" style={panel}>
        <div style={{ ...microLabel, fontSize: 10.5, marginBottom: 6 }}>CE subcontract{sub.ce_subcontracts.length === 1 ? '' : 's'}</div>
        {sub.ce_subcontracts.length ? (
          <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
            {sub.ce_subcontracts.map((c) => <div key={c.number} className="mono">#{c.number}{c.contract_date ? <span style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-3)' }}> · {fmtDay(c.contract_date)}</span> : null}</div>)}
          </div>
        ) : <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>None in CE for this job</div>}
        {canOpenSubs && (
          <a href={`/subcontractors/${sub.id}`} target="_blank" rel="noopener noreferrer" style={{ ...linkButton, marginTop: 14 }}>
            <Icon name="external-link" size={14} />Manage in Subcontractor Mgmt
          </a>
        )}
      </section>
    </div>
  );
}

/** A reminder to add a Documents task (Duncan 2026-10-24: "not every job will have close out documents ... But I do want
    to remind the project manager that that task for getting documents is there"): once, at the top of the tab, while
    the job isn't done and a sub with tasks here has no Documents task. Each person dismisses it for themselves, per
    job (this browser). */
const remindKey = (user, projectId) => `summit.docsReminder.${(user && (user.id || user.email)) || 'me'}.${projectId}`;
function DocsReminder({ subs, job, user, projectId, onOpenSchedule }) {
  const key = remindKey(user, projectId);
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(key) === '1'; } catch (e) { return false; } });
  const missing = subs.filter((s) => s.tasks.length && !s.tasks.some((t) => t.task_type === 'Documents')).map((s) => s.name);
  if (dismissed || !missing.length || (job && !job.needs_papers)) return null;
  const dismiss = () => { try { localStorage.setItem(key, '1'); } catch (e) { /* private window: hidden until reload */ } setDismissed(true); };
  return (
    <div role="note" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 12, background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 35%, transparent)' }}>
      <span style={{ color: 'var(--accent-text)', marginTop: 2, flexShrink: 0 }}><Icon name="template" size={18} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 650, color: 'var(--text-1)' }}>Closeout documents from the subs?</div>
        <div style={{ fontSize: 13.5, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
          If this job needs O&amp;M manuals, as-builts, training records, warranties or submittals from a sub, add a task on the
          Schedule, set its type to Documents, and pick the sub as its Responsible Party. No Documents task yet
          for: {missing.join(', ')}.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <SecondaryButton onClick={onOpenSchedule} style={{ height: 30, fontSize: 13 }}>Open the Schedule</SecondaryButton>
          <SecondaryButton onClick={dismiss} style={{ height: 30, fontSize: 13 }}>Dismiss</SecondaryButton>
        </div>
      </div>
    </div>
  );
}

/** A sub's photo reports waiting for a closeout Documents task, and what to add (Duncan 2026-10-25, the mockup he OK'd). */
function ReportsWaiting({ n, sub, onOpenSchedule }) {
  return (
    <div role="status" style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-1)',
      background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 22%, transparent)' }}>
      <div style={{ fontWeight: 650, color: 'var(--warning)' }}>
        {n} photo report{n === 1 ? ' is' : 's are'} waiting for a closeout Documents task
      </div>
      <div style={{ marginTop: 2, color: 'var(--text-2)' }}>
        Add one on the Schedule for {sub}: type Documents, in the Closeout phase (or named O&amp;M, As-Builts or Training). E3 Subs
        makes the reports within a minute or two after.
      </div>
      <SecondaryButton onClick={onOpenSchedule} style={{ height: 30, fontSize: 13, marginTop: 8 }}>Open the Schedule</SecondaryButton>
    </div>
  );
}

/** The bar the ticked tasks no sub is on get: how many, and the sub to assign them to. */
function PickBar({ count, verb, pickerLabel, excludeId, onPick, onClear, busy }) {
  const [text, setText] = useState('');
  if (!count) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 12px', margin: '2px 0 8px', borderRadius: 9, background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)' }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent-text)' }}>{count} selected</span>
      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{verb}</span>
      <div style={{ flex: '1 1 240px', maxWidth: 340 }}>
        <ResponsiblePartyField
          subsOnly excludeId={excludeId} value={text} subcontractorId={null} ariaLabel={pickerLabel}
          placeholder="Type a sub’s name" inputStyle={pickerStyle}
          onChange={(p) => { if (p.subcontractor_id) { setText(''); onPick({ id: p.subcontractor_id, name: p.responsible_party }); } else setText(p.responsible_party); }}
        />
      </div>
      <button type="button" onClick={onClear} disabled={busy} style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Clear</button>
    </div>
  );
}

export default function ProjectSubcontractorsTab({ projectId, user }) {
  const { testDate } = useProjectToday();
  const today = testDate || todayYmd();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // The card lifted out of the list over the blurred page, filling the window (Duncan 2026-10-27: "poping out the sub
  // that you click on and making it larger in the screen"; Lift.js): { key: a sub's id or NONE, rect, from }.
  const [lift, setLift] = useState(null);
  const liftRef = useRef(null);
  liftRef.current = lift;
  const [tabs, setTabs] = useState(() => new Map()); // an open sub's tab; Schedule first (Duncan 2026-10-22)
  const [picked, setPicked] = useState(() => new Set());
  // Per sub (or NONE): the buildings folded -- on its Schedule and Tasks alike -- and its Tasks filter (2026-10-27).
  const [folded, setFolded] = useState(() => new Map());
  const [taskFilters, setTaskFilters] = useState(() => new Map());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  // A task opened here shows its work, not its schedule properties (Duncan 2026-10-22: "the schedule changes only
  // happen in our actual construction schedule").
  const [workTaskId, setWorkTaskId] = useState(null);
  const [workLineId, setWorkLineId] = useState(null); // opened at a line (Requests and notes, 2026-10-24)
  const openLine = useCallback((taskId, lineId) => { setWorkLineId(lineId); setWorkTaskId(taskId); }, []);
  // Open requests per sub (2026-10-24): what its Requests and notes list counted, until then E3 Subs' own count.
  const [reqCounts, setReqCounts] = useState(() => new Map());
  const countRequests = useCallback((subId, n) => setReqCounts((m) => (m.get(subId) === n ? m : new Map(m).set(subId, n))), []);
  const [reqKey, setReqKey] = useState(0); // read the lists again after the Work window closes
  const { confirm, confirmDialogProps } = useConfirm();
  const canOpenSubs = can(user, 'subs.manage');
  const [jobDoc, setJobDoc] = useState(null); // { mode: 'add', kind, sub, replacing? } | { mode: 'edit', doc, sub }
  const [viewing, setViewing] = useState(null); // a document open in the viewer: { doc, sub }
  const [focus, setFocus] = useState(null); // the Documents row a flag asked for: { sub, key, seq }
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  // The list's search (the bar at the top, as on every tab), filter and order (Duncan 2026-10-27: every sub shows, those
  // that need something first).
  const query = usePageSearch('Search subs, trades or contacts…');
  const [view, setView] = useState('all');
  const [order, setOrder] = useState('attention');

  const load = useCallback(() => getProjectSubcontractors(projectId)
    .then((d) => { setData(d); setError(null); })
    .catch((e) => setError(e.message || 'Couldn’t load the subcontractors')), [projectId]);
  useEffect(() => { load(); }, [load]);
  // The sub side's progress per task, from E3 Subs (2026-10-23): what fills a sub's schedule bars. Read again when a
  // Work window closes (its lines may have just been marked complete). Without line work: none (bars as before).
  const [fills, setFills] = useState(null);
  const loadFills = useCallback(() => getProjectWorkProgress(projectId)
    .then((p) => setFills(p && p.on && !p.error ? p.tasks || {} : null))
    .catch(() => setFills(null)), [projectId]);
  useEffect(() => { loadFills(); }, [loadFills]);
  useEffect(() => { if (!note) return undefined; const t = setTimeout(() => setNote(null), 5000); return () => clearTimeout(t); }, [note]);

  const subs = useMemo(() => (data ? data.subcontractors.map((s) => ({
    ...s, needs: needsOf(s, today, data.job, fills), done: progressOf(s.tasks, fills), span: spanOf(s.tasks), nowNext: nowNextOf(s.tasks, today, fills),
  })) : []), [data, today, fills]);
  const assignedTasks = useMemo(() => subs.flatMap((s) => s.tasks), [subs]);
  // A lifted sub gone from the job on a reload: nothing to show.
  useEffect(() => {
    if (lift && lift.key !== NONE && data && !data.subcontractors.some((x) => x.id === lift.key)) setLift(null);
  }, [lift, data]);

  // Lift a card out of the list, from where it sits (its row's `data-psub`); one at a time.
  const liftCard = useCallback((key) => {
    if (liftRef.current && liftRef.current.key === key) return;
    const el = document.querySelector(`[data-psub="${key}"]`);
    if (!el) return;
    const r = el.getBoundingClientRect();
    setLift({ key, rect: { left: r.left, top: r.top, width: r.width, height: r.height }, from: el.querySelector('[data-psub-head]') || el });
  }, []);
  // A flag goes to its spot (Duncan 2026-10-22): the sub's card lifted on Documents at that row, highlighted -- or, for
  // late tasks and tasks with no work or documents (all of them, past the first ten), its Tasks tab.
  // 'billing' (2026-10-29, a task window's Sub SOV links with no lines yet): its Billing tab.
  const goTo = useCallback((subId, flag) => {
    const toTasks = flag === 'late' || flag === 'tasks' || flag === 'work' || flag === 'docs' || flag === 'reports';
    const toBilling = flag === 'billing';
    liftCard(subId);
    setTabs((m) => new Map(m).set(subId, toTasks ? 'tasks' : toBilling ? 'billing' : 'documents'));
    if (toTasks) setTaskFilters((m) => new Map(m).set(subId, flag === 'late' ? 'late' : flag === 'work' || flag === 'docs' ? 'work' : 'all'));
    if (!toTasks && !toBilling) setFocus((was) => ({ sub: subId, key: focusOfNeed(flag), seq: (was ? was.seq : 0) + 1 }));
  }, [liftCard]);
  // Subcontractor Mgmt's flags open a sub's spot here: ?sub=12&focus=coi | safety | msa | w9 | prequal.
  const askedSub = Number(params.get('sub')) || null;
  const askedFocus = params.get('focus') || 'documents';
  useEffect(() => {
    if (!data || !askedSub) return;
    if (data.subcontractors.some((x) => x.id === askedSub)) goTo(askedSub, askedFocus);
    setParams({}, { replace: true });
  }, [data, askedSub, askedFocus, goTo, setParams]);
  const focusFor = (subId) => (focus && focus.sub === subId ? focus : null);

  const foldedOf = (key) => folded.get(key) || NO_FOLDS;
  const toggleFold = (key) => (place) => setFolded((m) => {
    const s = new Set(m.get(key) || []);
    if (s.has(place)) s.delete(place); else s.add(place);
    return new Map(m).set(key, s);
  });
  const filterOf = (key) => taskFilters.get(key) || 'all';
  const setFilter = (key) => (f) => setTaskFilters((m) => new Map(m).set(key, f));
  const tabOf = (id) => tabs.get(id) || 'schedule';
  // A sub's open requests on the job: its list's own count once read, else E3 Subs' per task (tasks.progress), else 0.
  const openOf = (s) => (reqCounts.has(s.id) ? reqCounts.get(s.id)
    : fills ? s.tasks.reduce((n, t) => n + ((fills[t.id] && fills[t.id].openRequests) || 0), 0) : 0);
  const setTab = (id, t) => setTabs((m) => new Map(m).set(id, t));
  const onKeyLift = (key) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); liftCard(key); } };

  const assign = async (tasks, sub, verb) => {
    const n = tasks.length;
    const ok = await confirm({
      title: `${verb} ${n} task${n === 1 ? '' : 's'} to ${sub.name}?`,
      message: `${sub.name} becomes the Responsible Party on ${n === 1 ? 'it' : 'them'}. The dates don’t change, and each change goes in the schedule’s change log.`,
      confirmLabel: verb,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await assignTasksToSub(tasks.map((t) => t.id), sub.id);
      setNote(`${r.updated} task${r.updated === 1 ? '' : 's'} now with ${r.responsible_party}`);
      setPicked((s) => { const next = new Set(s); tasks.forEach((t) => next.delete(t.id)); return next; });
      setTab(sub.id, 'tasks'); // its card opens on them
      await load();
    } catch (e) {
      setError(e.message || 'Couldn’t move the tasks');
    } finally {
      setBusy(false);
    }
  };

  // A sub's documents for this job: add (the file, then its details), replace, edit, remove, download.
  const saveJobDoc = async (details, file, scan) => {
    const { mode, sub, replacing } = jobDoc;
    if (mode === 'add') await uploadJobDocument(projectId, sub.id, file, details, scan);
    else {
      // A subcontract's amount is the job's (its billing builds on it), not the file's: saved on its own (2026-10-29).
      const { amount_cents: cents, ...fields } = details;
      await updateSubcontractorDocument(jobDoc.doc.id, Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v === '' ? null : v])));
      if (cents !== undefined && (!sub.contract || Number(cents) !== sub.contract.amount_cents)) await setSubContractAmount(projectId, sub.id, Number(cents));
    }
    // Replace: the new one in, the old one off the list (its file kept in storage).
    if (replacing) await removeSubcontractorDocument(replacing.id).catch((e) => setError(e.message || 'The new one is in, but the old one couldn’t be removed'));
    setNote(mode === 'add' ? `${jobDocKindOf(details.doc_type).title} ${replacing ? 'replaced' : 'added'} for ${sub.name}` : 'Saved');
    setJobDoc(null);
    await load();
  };
  // A certificate's check, clicked: saved at once (its Undo is the same click back). The COI box and to-do line follow.
  const toggleCheck = async (doc, key, value) => {
    const next = await updateSubcontractorDocument(doc.id, { [key]: value });
    setData((d) => ({ ...d, subcontractors: d.subcontractors.map((x) => ({ ...x, documents: x.documents.map((y) => (y.id === doc.id ? { ...y, ...next } : y)) })) }));
  };
  const removeJobDoc = async (doc, sub) => {
    const ok = await confirm({
      title: `Remove ${doc.filename}?`,
      message: `It comes off ${sub.name}’s documents for this job. The file is kept in storage.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await removeSubcontractorDocument(doc.id);
      await load();
    } catch (e) {
      setError(e.message || 'Couldn’t remove the document');
    }
  };
  const downloadDoc = async (doc) => {
    try {
      await downloadSubcontractorDocument(doc);
    } catch (e) {
      setError(e.message || 'Download failed');
    }
  };

  if (error && !data) return <div style={{ padding: 4 }}><ErrorBanner>{error}</ErrorBanner></div>;
  if (!data) return <div style={{ padding: 24, color: 'var(--text-3)', fontSize: 14 }}>Loading the subcontractors…</div>;

  const total = data.total_tasks;
  // Needs attention: anything its card flags -- papers, standing, late tasks, no work or documents -- or open requests.
  const needy = (s) => s.needs.length > 0 || openOf(s) > 0;
  const unassigned = data.unassigned;
  const q = query.trim().toLowerCase();
  const found = q ? subs.filter((s) => [s.name, ...s.trades, s.contact && s.contact.name].filter(Boolean).join(' ').toLowerCase().includes(q)) : subs;
  const nNeedy = found.filter(needy).length;
  const shown = found.filter((s) => (view === 'attention' ? needy(s) : view === 'ok' ? !needy(s) : true))
    .sort((a, b) => (order === 'attention' ? Number(needy(b)) - Number(needy(a)) : 0)
      || (order === 'soonest' ? whenOf(a, today).localeCompare(whenOf(b, today)) : 0)
      || a.name.localeCompare(b.name));
  // A to-do's click: its requests and notes, or its spot (goTo).
  const doTodo = (s, key) => {
    if (key !== 'requests') { goTo(s.id, key); return; }
    setTab(s.id, 'requests');
    liftCard(s.id);
  };
  const liftedSub = lift && lift.key !== NONE ? subs.find((s) => s.id === lift.key) : null;
  const banners = (
    <>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {note && (
        <div role="status" style={{ padding: '9px 12px', borderRadius: 9, fontSize: 13.5, color: 'var(--success)', background: 'var(--success-soft)', border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)' }}>{note}</div>
      )}
    </>
  );

  /* A sub's card head -- who, what it's on, its papers -- and the line of what's waiting on its work: the list's row (a
     click lifts the card), and the top of the lifted card (`inLift`: no chevron, room for its ×). */
  const headOf = (s, inLift) => {
    const ce = s.ce_subcontracts[s.ce_subcontracts.length - 1];
    const contact = s.contact || {};
    const todos = todosOf(s, today, data.job, fills, openOf(s));
    const who = contact.name || s.name;
    const padR = inLift ? 56 : 18;
    const grid = { display: 'grid', gridTemplateColumns: HEAD_COLS, gap: '8px 18px', alignItems: 'center', padding: `13px ${padR}px 13px 18px` };
    const props = inLift
      ? { className: 'psub-head', style: { ...grid, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' } }
      : {
        role: 'button', tabIndex: 0, 'aria-haspopup': 'dialog', 'aria-label': `Open ${s.name}`, 'data-psub-head': true,
        onClick: () => liftCard(s.id), onKeyDown: onKeyLift(s.id), className: 'psub-head sub-row', style: { ...grid, cursor: 'pointer' },
      };
    return (
      <div {...props}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <Avatar initials={initialsOf(s.name)} size={36} accent={s.tasks.length > 0} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
              {s.standing !== 'approved' && <StandingPill standing={s.standing} />}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 1, minWidth: 0 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-3)', marginRight: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {[s.trades.length ? s.trades.join(', ') : 'No trade set', contact.name].filter(Boolean).join(' · ')}
              </span>
              {contact.phone && (
                <a className="psub-reach" href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`} title={`Call ${who}: ${contact.phone}`} aria-label={`Call ${who}, ${contact.phone}`}
                  onClick={(e) => e.stopPropagation()}><Icon name="phone" size={13} /></a>
              )}
              {contact.email && (
                <a className="psub-reach" href={`mailto:${contact.email}`} title={`Email ${who}: ${contact.email}`} aria-label={`Email ${who}, ${contact.email}`}
                  onClick={(e) => e.stopPropagation()}><Icon name="mail" size={13} /></a>
              )}
            </div>
          </div>
        </div>
        <div className="psub-mid" style={{ minWidth: 0 }}>
          {s.tasks.length ? (
            <>
              <NowNext nn={s.nowNext} today={today} onOpenTask={setWorkTaskId} />
              <div style={{ marginTop: 6 }}><Progress pct={s.done} /></div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {s.tasks.length} task{s.tasks.length === 1 ? '' : 's'}{s.span ? ` · ${fmtSpan(s.span[0], s.span[1], today)}` : ''}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{ce ? `No tasks yet · CE sub #${ce.number}` : 'No tasks yet'}</div>
          )}
        </div>
        <div className="psub-papers" style={{ display: 'flex', gap: 5 }}>
          {papersOf(s, today, data.job).map((p) => (p.key === 'msa' || p.key === 'w9' ? (
            <PaperBox key={p.key} href={canOpenSubs ? `/subcontractors/${s.id}?tab=documents&focus=${p.key}` : undefined}
              paper={{ ...p, title: canOpenSubs ? `${p.title}: open the sub in Subcontractor Mgmt` : `${p.title}: collected in Subcontractor Mgmt (Admin, VP, Accounting)` }} />
          ) : (
            <PaperBox key={p.key} paper={p} onClick={p.tone === 'na' ? undefined : () => goTo(s.id, p.key)} />
          )))}
        </div>
        {!inLift && <span className="psub-chev" style={{ display: 'flex', justifyContent: 'flex-end' }}><Chevron open={false} /></span>}
        {/* What's waiting on its work, one line (Duncan 2026-10-27), each a click to its spot. */}
        {todos.length > 0 && (
          <div style={{
            gridColumn: '1 / -1', display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 4, margin: `2px -${padR}px -13px -18px`,
            padding: '8px 18px', borderTop: '1px solid var(--border-subtle)', fontSize: 13,
          }}>
            <span style={{ display: 'inline-flex', marginRight: 8, color: TONE[todos[0].tone].color }}><Icon name="alert-triangle" size={14} stroke={2} /></span>
            {todos.map((x, i) => (
              <React.Fragment key={`${x.key}-${i}`}>
                {i > 0 && <span aria-hidden="true" style={{ margin: '0 8px', color: 'var(--text-4)' }}>·</span>}
                <button type="button" className="psub-todo-item" title={x.title} onClick={(e) => { e.stopPropagation(); doTodo(s, x.key); }}
                  style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: TONE[x.tone].color }}>
                  {x.text}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* A lifted sub's details: what was done (or went wrong) on it, and its tabs -- the Schedule first. */
  const detailOf = (s) => {
    const papers = papersOf(s, today, data.job); // as the boxes on top: the Documents tab counts them
    const tab = tabOf(s.id);
    const noTasks = <div style={emptyNote}>No tasks on this job yet. Close this and open “tasks with no subcontractor” to tick them and assign them to {s.name}.</div>;
    return (
      <div style={{ padding: '12px 18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {banners}
        <div>
          {/* The card's tabs on their own underlined bar (Duncan 2026-10-24: they ran into the Gantt's buttons; the
              small toggle of before was "very easy to miss", 10-22). */}
          <div role="tablist" aria-label={`${s.name}: details`}
            style={{ display: 'flex', gap: 20, marginBottom: 14, borderBottom: '1.5px solid var(--border-default)', overflowX: 'auto' }}>
            {TABS.map(([value, label]) => (
              <CardTab key={value} active={tab === value} onClick={() => { if (focus && focus.sub === s.id) setFocus(null); setTab(s.id, value); }}
                label={value === 'tasks' || value === 'documents' || (value === 'requests' && openOf(s) > 0)
                  ? <>{label}<span className="mono" style={{ marginLeft: 7, fontSize: 12.5, fontWeight: 500, color: value === 'requests' ? 'var(--warning)' : 'var(--text-3)' }}>{value === 'tasks' ? s.tasks.length : value === 'requests' ? openOf(s) : `${papers.filter((p) => p.tone === 'ok').length} of ${papers.filter((p) => p.tone !== 'na').length}`}</span></>
                  : label} />
            ))}
          </div>
          {tab === 'schedule' && (s.tasks.some((t) => day(t.start_date) && day(t.finish_date))
            ? <SubGantt tasks={s.tasks} today={today} fills={fills} onOpenTask={setWorkTaskId} folded={foldedOf(s.id)} onFold={toggleFold(s.id)} />
            : s.tasks.length ? <div style={emptyNote}>Its tasks have no dates yet.</div> : noTasks)}
          {tab === 'requests' && (
            <SubRequests key={reqKey} projectId={projectId} subId={s.id} onOpenLine={openLine} onCount={(n) => countRequests(s.id, n)} />
          )}
          {tab === 'tasks' && reportsWaiting(s, fills) > 0 && (
            <ReportsWaiting n={reportsWaiting(s, fills)} sub={s.name} onOpenSchedule={() => navigate(`/projects/${projectId}/tasks?reports=${s.id}`)} />
          )}
          {tab === 'tasks' && (s.tasks.length > 0 ? (
            <TaskList tasks={s.tasks} groupKey={s.id} onOpenTask={setWorkTaskId} today={today} progress={fills}
              filter={filterOf(s.id)} onFilter={setFilter(s.id)} folded={foldedOf(s.id)} onFold={toggleFold(s.id)} />
          ) : noTasks)}
          {tab === 'documents' && (
            <>
              <DocSection title="For this job" note={data.job.needs_papers ? null : 'the job is done: nothing more is needed'}>
                <JobDocRows job={data.job} docs={s.documents} contract={s.contract} today={today} wording={data.coi_wording}
                  focus={focusFor(s.id)}
                  onAdd={(kind, file) => setJobDoc({ mode: 'add', kind, sub: s, file })} onReplace={(doc) => setJobDoc({ mode: 'add', kind: doc.doc_type, sub: s, replacing: doc })}
                  onToggleCheck={toggleCheck} onEdit={(doc) => setJobDoc({ mode: 'edit', doc, sub: s })} onRemove={(doc) => removeJobDoc(doc, s)}
                  onView={(doc) => setViewing({ doc, sub: s })} onDownload={downloadDoc} />
              </DocSection>
              {/* Its company papers as in Subcontractor Mgmt (Duncan 2026-10-27, "All four papers on Documents"): where they
                  stand, read only -- they're kept there (Admin, VP, Accounting; a W-9 carries the tax ID). */}
              <DocSection title="Company papers" note={canOpenSubs ? 'kept in Subcontractor Mgmt' : 'kept in Subcontractor Mgmt (Admin, VP, Accounting)'}
                action={canOpenSubs ? (
                  <a href={`/subcontractors/${s.id}?tab=documents`} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', textDecoration: 'none' }}>Open in Subcontractor Mgmt</a>
                ) : null}>
                <CompanyDocRows company={{ msa: s.msa, w9: s.w9 ? {} : null }} history={{ msa: [], w9: [] }} kinds={['msa', 'w9']}
                  canAdd={false} canOpen={false} focus={focusFor(s.id)} today={today} />
              </DocSection>
              <DocSection title="Closeout documents" note="what the sub sends on its Documents tasks">
                <CloseoutDocs tasks={s.tasks.filter(isDocTask)} fills={fills} today={today} onOpen={setWorkTaskId} />
              </DocSection>
            </>
          )}
          {tab === 'contact' && <ContactCard sub={s} fills={fills} canOpenSubs={canOpenSubs} />}
          {tab === 'billing' && (
            <SubBilling projectId={projectId} sub={s}
              onOpenDocuments={() => { setFocus((was) => ({ sub: s.id, key: 'subcontract', seq: (was ? was.seq : 0) + 1 })); setTab(s.id, 'documents'); }} />
          )}
        </div>
      </div>
    );
  };

  // The tasks no sub is on: their row's words, and (lifted) their list to tick and assign.
  const noneWords = (
    <>
      <strong style={{ fontWeight: 700, color: 'var(--text-1)' }}>{unassigned.length}</strong> task{unassigned.length === 1 ? '' : 's'} with no subcontractor
      <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}> · E3’s own, typed names, or not set. Tick them to assign to a sub.</span>
    </>
  );
  const noneTicked = unassigned.filter((t) => picked.has(t.id));
  const noneDetail = (
    <div style={{ padding: '8px 18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {banners}
      {unassigned.length ? (
        <TaskList tasks={unassigned} groupKey={NONE} picked={picked} setPicked={setPicked} onOpenTask={setWorkTaskId} today={today}
          filter={filterOf(NONE)} onFilter={setFilter(NONE)} folded={foldedOf(NONE)} onFold={toggleFold(NONE)}>
          <PickBar count={noneTicked.length} verb="Assign to a sub" pickerLabel="The sub to assign the ticked tasks to" excludeId={null} busy={busy}
            onPick={(to) => assign(noneTicked, to, 'Assign')}
            onClear={() => setPicked((p) => { const n = new Set(p); unassigned.forEach((t) => n.delete(t.id)); return n; })} />
        </TaskList>
      ) : <div style={emptyNote}>Every task has a subcontractor now.</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <DocsReminder key={projectId} subs={subs} job={data.job} user={user} projectId={projectId} onOpenSchedule={() => navigate(`/projects/${projectId}/tasks`)} />
      {/* (in the lifted card instead, while one is up) */}
      {!lift && banners}

      <SubStyles />
      <style>{`
        .psub-reach { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 6px; color: var(--text-3); flex-shrink: 0; }
        .psub-reach:hover { color: var(--accent-text); background: var(--bg-hover); }
        .psub-task:hover { color: var(--accent-text) !important; text-decoration: underline; }
        .psub-todo-item:hover { text-decoration: underline; }
        @media (max-width: 900px) {
          .psub-head { grid-template-columns: minmax(0, 1fr) 16px !important; }
          .psub-mid, .psub-papers { grid-column: 1 / -1; }
          .psub-chev { grid-row: 1; grid-column: 2; }
        }
      `}</style>
      {subs.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px 14px', flexWrap: 'wrap' }}>
          <SegmentedControl value={view} onChange={setView} options={[
            { value: 'attention', label: 'Needs attention', count: nNeedy, countColor: nNeedy ? 'var(--danger)' : undefined },
            { value: 'ok', label: 'In order', count: found.length - nNeedy },
            { value: 'all', label: 'All', count: found.length },
          ]} />
          {/* The tab's four tiles, folded in here (Duncan 2026-10-27: the top of the page took "almost half of the
              screen"); the subs and those needing attention are the counts beside it. */}
          <span style={{ fontSize: 13.5, color: 'var(--text-3)' }} title="Milestones aside; each task by its days, as far along as E3 Subs says where it has its progress, else by the schedule">
            <span className="mono" style={{ color: 'var(--text-1)' }}>{assignedTasks.length}</span> of {total} task{total === 1 ? '' : 's'} {assignedTasks.length === 1 ? 'has' : 'have'} a
            sub · <span className="mono" style={{ color: 'var(--text-1)' }}>{progressOf(assignedTasks, fills)}%</span> done
          </span>
          <span style={{ flex: 1 }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-3)' }}>
            Sort
            <select value={order} onChange={(e) => setOrder(e.target.value)} style={sortStyle}>
              {ORDERS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </select>
          </label>
        </div>
      )}
      <section aria-label="Subcontractors" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!subs.length && (
          <div style={{ ...card, padding: '18px 18px', fontSize: 14, color: 'var(--text-3)' }}>
            No subcontractors on this job yet. Pick one as a task’s Responsible Party on the Schedule, or assign the tasks below.
          </div>
        )}
        {subs.length > 0 && !shown.length && (
          <div style={{ ...card, padding: '16px 18px', fontSize: 14, color: 'var(--text-3)' }}>
            {q ? <><span style={{ color: 'var(--text-1)', fontWeight: 650 }}>{noMatchText(query).title}</span> · {noMatchText(query).body}</>
              : view === 'attention' ? 'No sub needs anything right now.' : 'No sub is fully in order yet.'}
            {!q && (
              <button type="button" onClick={() => setView('all')}
                style={{ marginLeft: 8, padding: 0, border: 'none', background: 'transparent', color: 'var(--accent-text)', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>
                Show all
              </button>
            )}
          </div>
        )}
        {shown.map((s) => (
          <article key={s.id} data-psub={s.id} aria-label={s.name}
            style={{ ...card, overflow: 'hidden', visibility: lift && lift.key === s.id ? 'hidden' : undefined }}>
            {headOf(s, false)}
          </article>
        ))}
        {unassigned.length > 0 && (
          <article data-psub={NONE} aria-label="Tasks with no subcontractor"
            style={{ ...card, overflow: 'hidden', borderStyle: 'dashed', boxShadow: 'none', visibility: lift && lift.key === NONE ? 'hidden' : undefined }}>
            <div role="button" tabIndex={0} aria-haspopup="dialog" data-psub-head onClick={() => liftCard(NONE)} onKeyDown={onKeyLift(NONE)} className="sub-row"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', cursor: 'pointer', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: 'var(--text-2)', flex: 1, minWidth: 220 }}>{noneWords}</span>
              <Chevron open={false} />
            </div>
          </article>
        )}
      </section>

      {lift && (lift.key === NONE || liftedSub) && (
        <Lift rect={lift.rect} from={lift.from} fill cardStyle={{ background: 'var(--bg-surface-2)' }}
          ariaLabel={lift.key === NONE ? 'Tasks with no subcontractor' : liftedSub.name}
          paused={!!workTaskId || !!jobDoc || !!viewing || !!confirmDialogProps.open}
          onClosed={() => {
            const key = lift.key;
            setFocus((f) => (f && f.sub === key ? null : f));
            setTaskFilters((m) => { if (!m.has(key)) return m; const n = new Map(m); n.delete(key); return n; });
            setLift(null);
          }}
          line={lift.key === NONE ? (
            <div style={{ padding: '14px 56px 14px 18px', fontSize: 14, color: 'var(--text-2)', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' }}>{noneWords}</div>
          ) : headOf(liftedSub, true)}>
          {lift.key === NONE ? noneDetail : detailOf(liftedSub)}
        </Lift>
      )}

      {/* Closed: the lists read again -- its work may be in now (the no-work flag goes), its lines marked complete. */}
      {workTaskId && (() => {
        const closeWork = () => { setWorkTaskId(null); setWorkLineId(null); setReqKey((k) => k + 1); loadFills(); load(); };
        // Its Billing tab's links to the sub's spot (2026-10-29): the Subcontract card on Documents, or its Billing tab.
        return <TaskWorkDialog taskId={workTaskId} lineId={workLineId} canSendToCloseout={can(user, 'closeout.manage')} onClose={closeWork}
          onOpenSub={(subId, where) => { closeWork(); goTo(subId, where); }} />;
      })()}
      {jobDoc && (
        <JobDocDialog mode={jobDoc.mode} kind={jobDoc.kind} doc={jobDoc.doc} job={data.job} projectId={projectId} subName={jobDoc.sub.name}
          replacing={jobDoc.replacing} initialFile={jobDoc.file} contract={jobDoc.sub.contract} ceSubcontracts={jobDoc.sub.ce_subcontracts || []}
          onClose={() => setJobDoc(null)} onSubmit={saveJobDoc} />
      )}
      {viewing && (
        <DocViewer doc={viewing.doc} subtitle={`${viewing.doc.title} · ${viewing.sub.name}`} onDownload={downloadDoc} onClose={() => setViewing(null)} />
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
